const dashPattern = new RegExp(String.fromCharCode(0x2014) + "|&(?:amp;)*(?:mdash;|#0*8212(?:;|(?![0-9]))|#x0*2014(?:;|(?![0-9a-f])))", "gi");
const attributes = ["title", "alt", "aria-label", "aria-description", "placeholder"];
const preserve = "script,style,input,textarea,code,pre,[contenteditable=true],[data-typography-preserve]";

export const normalizeDisplayText = (value) => typeof value === "string" ? value.replace(dashPattern, "-") : value;

// Render stored/streamed prose consistently without rewriting React state, records,
// form values, credentials, hrefs, tokens or element identities.
// Placeholders, labels and tooltips are UI copy, including on login controls.
// They are intentionally normalized; actual value/defaultValue attributes are not.
export function installTextStyleGuard(doc = document) {
  const cleanText = (node) => {
    if (node.parentElement?.closest(preserve)) return;
    const next = normalizeDisplayText(node.nodeValue);
    if (next !== node.nodeValue) node.nodeValue = next;
  };
  const visit = (node) => {
    if (node.nodeType === Node.TEXT_NODE) { cleanText(node); return; }
    if (node.nodeType !== Node.ELEMENT_NODE || node.closest("script,style,[data-typography-preserve]")) return;
    for (const element of [node, ...node.querySelectorAll("[title],[alt],[aria-label],[aria-description],[placeholder],iframe[srcdoc]")]) {
      if (element.closest("[data-typography-preserve]")) continue;
      for (const attr of attributes) {
        if (!element.hasAttribute(attr)) continue;
        const previous = element.getAttribute(attr);
        const next = normalizeDisplayText(previous);
        if (next !== previous) element.setAttribute(attr, next);
      }
      if (element.tagName === "IFRAME" && element.hasAttribute("srcdoc")) {
        const html = element.getAttribute("srcdoc");
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const textNodes = parsed.createTreeWalker(parsed.documentElement, NodeFilter.SHOW_TEXT);
        let changed = false;
        while (textNodes.nextNode()) {
          const item = textNodes.currentNode;
          if (item.parentElement?.closest(preserve)) continue;
          const next = normalizeDisplayText(item.nodeValue);
          if (next !== item.nodeValue) { item.nodeValue = next; changed = true; }
        }
        if (changed) element.setAttribute("srcdoc", "<!doctype html>" + parsed.documentElement.outerHTML);
      }
    }
    const walker = doc.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) cleanText(walker.currentNode);
  };
  const observer = new MutationObserver((records) => {
    const roots = new Set();
    for (const record of records) {
      if (record.type === "characterData") cleanText(record.target);
      else if (record.type === "attributes") roots.add(record.target);
      else record.addedNodes.forEach((node) => roots.add(node));
    }
    roots.forEach(visit);
  });
  visit(doc.documentElement);
  observer.observe(doc.documentElement, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: [...attributes, "srcdoc"] });
  return () => observer.disconnect();
}